import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Input,
  Label,
  Spinner,
} from "@/Components/ui";
import BreadCrumb from "@/Components/Common/BreadCrumb";
import { Toaster, toast } from "react-hot-toast";
import CommonDatePicker from "@/Components/Common/CommonDatePicker";
import moment from "moment";
import MyEditor from "@/Components/editor/MyEditor";
import {
  noticeService,
  NoticeDetail,
  NoticePayload,
  NoticeImage,
} from "@/api/services/notice.service";
import { SITE_ID } from "@/assets/meta/meta";

const NoticeForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = id && id !== "0";

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [displayDate, setDisplayDate] = useState<Date | null>(null);
  const [expireDate, setExpireDate] = useState<Date | null>(null);
  const [isHighlight, setIsHighlight] = useState(false);
  const [isPublished, setIsPublished] = useState(true);

  const [images, setImages] = useState<NoticeImage[]>([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [linkUrls, setLinkUrls] = useState<Array<{ label: string; url: string }>>([]);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      fetchNotice(Number(id));
    }
  }, [id, isEditMode]);

  const fetchNotice = async (noticeId: number) => {
    try {
      setLoading(true);
      const response = await noticeService.getNotice(noticeId);
      const notice = response.data;
      fillFormData(notice);
    } catch (error) {
      console.error("Failed to fetch notice:", error);
      toast.error("공지사항을 불러오는데 실패했습니다.");
      navigate("/notice-list");
    } finally {
      setLoading(false);
    }
  };

  const fillFormData = (notice: NoticeDetail) => {
    setTitle(notice.title);
    setContent(notice.content);
    setDisplayDate(notice.display_date ? new Date(notice.display_date) : null);
    setExpireDate(notice.expire_date ? new Date(notice.expire_date) : null);
    setIsHighlight(notice.is_highlight);
    setIsPublished(notice.is_published);
    setImages(notice.images || []);
    setLinkUrls(notice.link_urls || []);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      toast.error("이미지 용량은 5MB 이하만 가능합니다.");
      return;
    }

    try {
      setUploadingImage(true);
      const response = await noticeService.uploadImage(file);
      const { asset_url, asset_key } = response.data;
      setImages((prev) => [
        ...prev,
        { asset_url, asset_key, sort_order: prev.length },
      ]);
      toast.success("이미지가 업로드되었습니다.");
    } catch (error) {
      console.error("Failed to upload image:", error);
      toast.error("이미지 업로드에 실패했습니다.");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeImage = (index: number) => {
    setImages((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      return updated.map((img, i) => ({ ...img, sort_order: i }));
    });
  };

  const moveImage = (index: number, direction: "up" | "down") => {
    setImages((prev) => {
      const newImages = [...prev];
      const targetIndex = direction === "up" ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= newImages.length) return prev;
      [newImages[index], newImages[targetIndex]] = [
        newImages[targetIndex],
        newImages[index],
      ];
      return newImages.map((img, i) => ({ ...img, sort_order: i }));
    });
  };

  const validateForm = (): boolean => {
    if (!title.trim()) {
      toast.error("제목을 입력해주세요.");
      return false;
    }
    if (!content.trim()) {
      toast.error("내용을 입력해주세요.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload: NoticePayload = {
      site: SITE_ID,
      title: title.trim(),
      content: content.trim(),
      display_date: displayDate ? moment(displayDate).format("YYYY-MM-DD") : null,
      expire_date: expireDate ? moment(expireDate).format("YYYY-MM-DD") : null,
      link_urls: linkUrls,
      is_highlight: isHighlight,
      is_published: isPublished,
      images: images.map((img) => ({
        asset_url: img.asset_url,
        asset_key: img.asset_key,
        sort_order: img.sort_order,
      })),
    };

    try {
      setSubmitting(true);
      if (isEditMode) {
        await noticeService.updateNotice(Number(id), payload);
        toast.success("공지사항이 수정되었습니다.");
      } else {
        await noticeService.createNotice(payload);
        toast.success("공지사항이 등록되었습니다.");
      }
      navigate("/notice-list");
    } catch (error) {
      console.error("Failed to save notice:", error);
      toast.error("저장에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-8">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Toaster position="top-center" />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={isEditMode ? "공지사항 수정" : "새 공지사항 작성"}
            pageTitle="공지사항"
          />

          <Row className="g-4 mb-3">
            <Col sm="auto">
              <h4 className="card-title mb-0">
                {isEditMode ? "공지사항 수정" : "새 공지사항 작성"}
              </h4>
            </Col>
            <Col sm="auto" className="ms-auto">
              <div className="d-flex gap-2">
                <Button
                  color="secondary"
                  onClick={() => navigate("/notice-list")}
                >
                  취소
                </Button>
                <Button
                  color="primary"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Spinner size="sm" className="me-1" /> 저장 중...
                    </>
                  ) : isEditMode ? (
                    "수정"
                  ) : (
                    "등록"
                  )}
                </Button>
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg={8}>
              <Card className="mb-3">
                <CardHeader>
                  <h5 className="card-title mb-0">기본 정보</h5>
                </CardHeader>
                <CardBody>
                  <div className="mb-3">
                    <Label className="form-label">제목 *</Label>
                    <Input
                      type="text"
                      placeholder="공지사항 제목을 입력하세요"
                      value={title}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setTitle(e.target.value)
                      }
                      maxLength={200}
                    />
                  </div>
                  <div className="mb-3">
                    <Label className="form-label">내용 *</Label>
                    <MyEditor
                      value={content}
                      onChange={(value) => setContent(value)}
                      uid="notice-content"
                    />
                  </div>
                </CardBody>
              </Card>

              <Card className="mb-3">
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">이미지</h5>
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleImageUpload}
                      style={{ display: "none" }}
                    />
                    <Button
                      color="soft-primary"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? (
                        <>
                          <Spinner size="sm" className="me-1" /> 업로드 중...
                        </>
                      ) : (
                        <>
                          <i className="ri-add-line me-1"></i>이미지 추가
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardBody>
                  {images.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      등록된 이미지가 없습니다.
                    </div>
                  ) : (
                    <div className="row g-3">
                      {images.map((img, index) => (
                        <div key={img.asset_key || index} className="col-md-4">
                          <div className="position-relative border rounded overflow-hidden">
                            <img
                              src={img.asset_url}
                              alt={`이미지 ${index + 1}`}
                              className="w-100"
                              style={{
                                height: "150px",
                                objectFit: "cover",
                              }}
                            />
                            <div className="position-absolute top-0 end-0 p-1">
                              <Button
                                color="danger"
                                size="sm"
                                className="btn-icon"
                                onClick={() => removeImage(index)}
                              >
                                <i className="ri-close-line"></i>
                              </Button>
                            </div>
                            <div className="position-absolute bottom-0 start-0 end-0 bg-dark bg-opacity-50 p-1 d-flex justify-content-center gap-1">
                              <Button
                                color="light"
                                size="sm"
                                disabled={index === 0}
                                onClick={() => moveImage(index, "up")}
                              >
                                <i className="ri-arrow-left-line"></i>
                              </Button>
                              <Button
                                color="light"
                                size="sm"
                                disabled={index === images.length - 1}
                                onClick={() => moveImage(index, "down")}
                              >
                                <i className="ri-arrow-right-line"></i>
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>

              <Card className="mb-3">
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h5 className="card-title mb-0">링크 ({linkUrls.length})</h5>
                  <Button
                    color="soft-primary"
                    size="sm"
                    onClick={() => setLinkUrls([...linkUrls, { label: "", url: "" }])}
                  >
                    <i className="ri-add-line me-1"></i>링크 추가
                  </Button>
                </CardHeader>
                <CardBody>
                  {linkUrls.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      추가된 링크가 없습니다.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {linkUrls.map((link, index) => (
                        <div key={index} className="p-3 border rounded">
                          <div className="mb-2">
                            <Label className="form-label small">링크 텍스트</Label>
                            <Input
                              type="text"
                              placeholder="예: 자세히 보기"
                              value={link.label}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newLinks = [...linkUrls];
                                newLinks[index].label = e.target.value;
                                setLinkUrls(newLinks);
                              }}
                            />
                          </div>
                          <div className="mb-2">
                            <Label className="form-label small">URL</Label>
                            <Input
                              type="text"
                              placeholder="https://example.com"
                              value={link.url}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const newLinks = [...linkUrls];
                                newLinks[index].url = e.target.value;
                                setLinkUrls(newLinks);
                              }}
                            />
                          </div>
                          <Button
                            color="soft-danger"
                            size="sm"
                            onClick={() => {
                              setLinkUrls(linkUrls.filter((_, i) => i !== index));
                            }}
                          >
                            <i className="ri-delete-bin-line me-1"></i>삭제
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col lg={4}>
              <Card className="mb-3">
                <CardHeader>
                  <h5 className="card-title mb-0">게시 설정</h5>
                </CardHeader>
                <CardBody>
                  <div className="mb-3">
                    <Label className="form-label">게시일</Label>
                    <CommonDatePicker
                      value={displayDate || undefined}
                      clearable
                      onChangeDates={(dates: Date[]) =>
                        setDisplayDate(dates[0] || null)
                      }
                      onClear={() => setDisplayDate(null)}
                      className="form-control"
                      placeholder="게시일 선택 (선택사항)"
                    />
                    <small className="text-muted">
                      미설정시 작성일이 표시됩니다.
                    </small>
                  </div>
                  <div className="mb-3">
                    <Label className="form-label">만료일</Label>
                    <CommonDatePicker
                      value={expireDate || undefined}
                      clearable
                      onChangeDates={(dates: Date[]) =>
                        setExpireDate(dates[0] || null)
                      }
                      onClear={() => setExpireDate(null)}
                      className="form-control"
                      placeholder="만료일 선택 (선택사항)"
                    />
                    <small className="text-muted">
                      설정시 해당 날짜 이후 일반 사용자에게 보이지 않습니다.
                    </small>
                  </div>
                  <div className="mb-3">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="isHighlight"
                        checked={isHighlight}
                        onChange={(e) => setIsHighlight(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="isHighlight">
                        하이라이트 (상단 고정)
                      </label>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="isPublished"
                        checked={isPublished}
                        onChange={(e) => setIsPublished(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="isPublished">
                        게시 상태
                      </label>
                    </div>
                    <small className="text-muted">
                      비활성화시 사용자에게 표시되지 않습니다.
                    </small>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default NoticeForm;
